/**
 * Single source of truth for the Pyodide runtime used by PythonNotebookV2.
 *
 * On every version bump: change PYODIDE_VERSION, recompute both hashes with
 *   curl -sL https://cdn.jsdelivr.net/pyodide/v<VERSION>/full/<file> | shasum -a 256
 * and update the loader copy (spec §9.4).
 */
export const PYODIDE_VERSION = '314.0.7'
export const PYODIDE_PYTHON = '3.14'

export const PYODIDE_CORE_SHA256: Readonly<Record<string, string>> = {
  'pyodide.asm.wasm': 'cc36e3cab04fdfc9a63ff13eb52eae2b911bf46c025cc7b281f394bd3de1d5e6', // 9,598,218 bytes
  'python_stdlib.zip': 'fa1957e5777068fc4f7437f96d860ae2fbe9c19732ba06c84e004ec16dd7dd7a', // 2,545,637 bytes
}

/** Packages loaded during the 'installing-packages' phase (spec §6.4). */
export const PYODIDE_PRELOAD_PACKAGES: readonly string[] = ['micropip', 'numpy', 'pandas', 'matplotlib', 'jedi']

const JSDELIVR_ROOT = 'https://cdn.jsdelivr.net/'
const JSDELIVR_BASE = `${JSDELIVR_ROOT}pyodide/v${PYODIDE_VERSION}/full/`

/** Existing per-browser opt-out shared with v1 (see PythonNotebook/pyodideConfig.ts). */
export const DISABLE_SELF_HOST_KEY = 'disable-pyodide-self-host'

/** Legacy JupyterLite service-worker cache that v1 populates. Cleared by "reset cache". */
export const LEGACY_CDN_CACHE_NAME = 'pyodide-cdn-v1'

export const pyodideCacheName = () => `xpl-pyodide-v${PYODIDE_VERSION}`

export interface AssetBases {
  primaryBase: string
  fallbackBase: string | null
}

export function resolveAssetBases(env: { hostname: string; origin: string; selfHostDisabled: boolean }): AssetBases {
  const isLocalhost = env.hostname === 'localhost' || env.hostname === '127.0.0.1'
  if (isLocalhost || env.selfHostDisabled) {
    return { primaryBase: JSDELIVR_BASE, fallbackBase: null }
  }
  return {
    primaryBase: `${env.origin}/pyodide-cdn/pyodide/v${PYODIDE_VERSION}/full/`,
    fallbackBase: JSDELIVR_BASE,
  }
}

/** jsDelivr root (direct, or via the /pyodide-cdn proxy which only strips that prefix). */
export function resolveJsDelivrRoot(env: { hostname: string; origin: string; selfHostDisabled: boolean }): string {
  const isLocalhost = env.hostname === 'localhost' || env.hostname === '127.0.0.1'
  return isLocalhost || env.selfHostDisabled ? JSDELIVR_ROOT : `${env.origin}/pyodide-cdn/`
}

function windowEnv() {
  let selfHostDisabled = false
  try {
    selfHostDisabled = window.localStorage.getItem(DISABLE_SELF_HOST_KEY) === '1'
  } catch {
    selfHostDisabled = false
  }
  return { hostname: window.location.hostname, origin: window.location.origin, selfHostDisabled }
}

/** Browser convenience wrapper; call only inside effects (spec §6.1). */
export function resolveAssetBasesFromWindow(): AssetBases {
  return resolveAssetBases(windowEnv())
}

export function resolveJsDelivrRootFromWindow(): string {
  return resolveJsDelivrRoot(windowEnv())
}
